import { useState, useEffect } from 'react';
import { client } from '@/api/client';
import { Printer, FileDown } from 'lucide-react';
import DateRangeFilter from './DateRangeFilter';
import { exportCSV, inRange, loadsForUnit } from './reportUtils';
import { useCompanyInfo, generatePrintHTML, openPrint } from './ReportExporter';
import { computeLoadGross, numOrDefault } from '@/lib/invoiceHelpers';

export default function UnitRevenueReport() {
  const [loads, setLoads] = useState([]);
  const [assets, setAssets] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [carrierPercent, setCarrierPercent] = useState(15);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [loading, setLoading] = useState(true);
  const company = useCompanyInfo();

  useEffect(() => {
    Promise.all([
      client.entities.Load.list('-delivery_date', 500),
      client.entities.Asset.filter({ asset_type: 'Truck' }, 'unit_number', 200),
      client.entities.DriverProfile.list('full_name', 100),
      client.entities.CompanySettings.list(),
    ]).then(([l, a, d, settings]) => {
      setLoads(l); setAssets(a); setDrivers(d);
      // Was hardcoded to 15% regardless of what Accounting configured in
      // Pay & Deductions, so this report's Carrier Take/Net to Carrier
      // columns could disagree with the real per-load Settlement amounts.
      const carrierSetting = settings.find(s => s.key === 'carrier_take_percent');
      setCarrierPercent(numOrDefault(carrierSetting?.value, 15));
      setLoading(false);
    });
  }, []);

  const filtered = loads.filter(l => inRange(l.delivery_date || l.pickup_date, start, end) && l.assigned_driver_email);

  const byUnit = {};
  assets.forEach(a => {
    if (!a.assigned_driver_email) return;
    const driverLoads = loadsForUnit(filtered, a);
    const driver = drivers.find(d => d.user_email === a.assigned_driver_email);
    // Was raw `l.rate || 0` -- missed fuel surcharge/detention/stop-off/
    // tarp/layover/extra fees that computeLoadGross accounts for, same
    // gap fixed in ActiveLoad.jsx's driver-facing breakdown.
    const gross = driverLoads.reduce((s, l) => s + computeLoadGross(l), 0);
    const miles = driverLoads.reduce((s, l) => s + (l.miles || 0), 0);
    const carrierTake = gross * (carrierPercent / 100);
    byUnit[a.unit_number] = {
      'Unit #': a.unit_number,
      Driver: driver?.full_name || a.assigned_driver_email || '—',
      'Load Count': driverLoads.length,
      'Total Miles': Math.round(miles),
      'Gross Revenue': gross,
      'Carrier Take': carrierTake,
      'Net to Carrier': carrierTake,
      'Revenue/Mile': miles > 0 ? (gross / miles).toFixed(2) : '—',
    };
  });

  const rows = Object.values(byUnit).sort((a, b) => b['Gross Revenue'] - a['Gross Revenue']);

  const csvRows = rows.map(r => ({
    ...r,
    'Gross Revenue': r['Gross Revenue'].toFixed(2),
    'Carrier Take': r['Carrier Take'].toFixed(2),
    'Net to Carrier': r['Net to Carrier'].toFixed(2),
  }));

  const handlePrint = () => {
    const cols = ['Unit #', 'Driver', 'Load Count', 'Total Miles', 'Gross Revenue', 'Carrier Take', 'Net to Carrier', 'Revenue/Mile'];
    const dataRows = rows.map(r => [
      { value: r['Unit #'], class: 'bold' }, r.Driver, r['Load Count'], r['Total Miles'].toLocaleString(),
      { value: `$${r['Gross Revenue'].toLocaleString()}`, class: 'positive' },
      { value: `$${r['Carrier Take'].toFixed(2)}`, class: 'negative' },
      { value: `$${r['Net to Carrier'].toFixed(2)}`, class: '' },
      r['Revenue/Mile'] !== '—' ? { value: `$${r['Revenue/Mile']}`, class: 'gold' } : '—',
    ]);
    const grandGross = rows.reduce((s, r) => s + r['Gross Revenue'], 0);
    const grandLoads = rows.reduce((s, r) => s + r['Load Count'], 0);
    const totalsRow = ['TOTALS', '', grandLoads, '', { value: `$${grandGross.toLocaleString()}`, class: 'gold' }, '', '', ''];
    const cards = [
      { label: 'Active Units', value: rows.length, color: 'gold' },
      { label: 'Total Loads', value: grandLoads, color: '' },
      { label: 'Gross Revenue', value: `$${grandGross.toLocaleString()}`, color: 'positive' },
    ];
    const html = generatePrintHTML('Unit Revenue Report', `${start || 'All'} – ${end || 'All'}`, cols, dataRows, totalsRow, company, cards);
    openPrint(html);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <DateRangeFilter onChange={(s, e) => { setStart(s); setEnd(e); }} onExport={() => exportCSV(csvRows, 'unit-revenue.csv')} exportDisabled={loading || rows.length === 0} />
        <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-border text-sm text-foreground hover:bg-white/10"><Printer className="w-4 h-4" /> Print</button>
        <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-border text-sm text-foreground hover:bg-white/10"><FileDown className="w-4 h-4" /> Export PDF</button>
      </div>
      <div className="glass-card overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border">
              {['Unit #', 'Driver', 'Load Count', 'Total Miles', 'Gross Revenue', 'Carrier Take', 'Net to Carrier', 'Revenue/Mile'].map(h => (
                <th key={h} className="text-left px-3 py-2.5 text-muted-foreground font-medium whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map(r => (
              <tr key={r['Unit #']} className="hover:bg-white/3">
                <td className="px-3 py-2.5 font-semibold text-primary">{r['Unit #']}</td>
                <td className="px-3 py-2.5 text-foreground">{r.Driver}</td>
                <td className="px-3 py-2.5 text-muted-foreground">{r['Load Count']}</td>
                <td className="px-3 py-2.5 text-muted-foreground">{r['Total Miles'].toLocaleString()}</td>
                <td className="px-3 py-2.5 font-semibold text-green-400">${r['Gross Revenue'].toLocaleString()}</td>
                <td className="px-3 py-2.5 text-red-400">${r['Carrier Take'].toFixed(2)}</td>
                <td className="px-3 py-2.5 text-foreground">${r['Net to Carrier'].toFixed(2)}</td>
                <td className="px-3 py-2.5 text-muted-foreground">{typeof r['Revenue/Mile'] === 'string' && r['Revenue/Mile'] !== '—' ? `$${r['Revenue/Mile']}` : r['Revenue/Mile']}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && !loading && <p className="text-center text-muted-foreground py-8 text-sm">No unit revenue data. Make sure assets have assigned drivers.</p>}
        {loading && <p className="text-center text-muted-foreground py-8 text-sm">Loading...</p>}
      </div>
    </div>
  );
}